import { useT } from "@gladis/ui/i18n";
import { useTheme, type ThemeChoice } from "@gladis/ui/theme";
import { ComputerDesktopIcon, MoonIcon, SunIcon } from "@heroicons/react/24/outline";
import type { ComponentType, ReactNode } from "react";

/**
 * The theme picker, as a rail control.
 *
 * ==========================================================================================
 * IT CYCLES THROUGH THREE STATES RATHER THAN OPENING A MENU
 * ==========================================================================================
 *
 * `ActionMenu` exists and would give three labelled radio-ish items, which is the more discoverable
 * control. It is the wrong one here for a reason that is structural rather than aesthetic: the rail
 * is `fixed` and 64px wide, pinned to the left edge above `md` and to the BOTTOM edge below it. A
 * popover anchored to a control in the phone bar opens off the bottom of the viewport unless it is
 * flipped, and the flipped placement has to clear a control that is already at the screen edge.
 *
 * A cycling button has no placement at all, is the same shape as every other pill, and states the
 * whole model in its accessible name — which a menu of three items does not, since a menu never
 * says what `System` currently resolves to.
 *
 * The order is Light -> Dark -> System -> Light. `System` sits after the two explicit choices
 * because it is the one you arrive at by deciding not to decide, and it is the default a fresh
 * visitor already has.
 *
 * ==========================================================================================
 * IT LIVES IN THE TOP BAR NOW, AND THAT IS WHY IT IS ICON-ONLY
 * ==========================================================================================
 *
 * It was a row in the navigation rail, borrowing the pill contract from `nav-pill.ts` so the seam
 * between it and `NavLink` was invisible. It moved to the chrome at the top right on 2026-08-14, for the
 * reason `side-nav.tsx` now records: THE RAIL IS A LIST OF PLACES, and a colour scheme is not a place.
 *
 * The pill shape did not come with it. On `md` that contract is a full-width left-aligned row with a
 * visible label, which in a 64px bar beside the household switcher would be a wide button reading
 * "Light" next to the household's name. So it is a compact round icon button, sized and shaped to match
 * the account menu's avatar trigger beside it — the two are one group and should read as one.
 *
 * THE VISIBLE WORD IS GONE; THE ACCESSIBLE NAME IS UNCHANGED. "Theme: Dark. Next: System." was always
 * the name and never the visible text, precisely because a rail row had no width for it, and
 * `theme.spec.ts` asserts all three names character for character. Dropping the label costs a sighted
 * reader the current state as a word, which `title` still gives on hover and the icon gives at a glance.
 */

/** What pressing it does. A total map, so there is no "and then?" case to get wrong. */
const NEXT: Record<ThemeChoice, ThemeChoice> = {
  light: "dark",
  dark: "system",
  system: "light",
};

/*
 * KEYS, NOT SENTENCES — feature 021. A module-scope `t()` is evaluated once at import and freezes
 * the label in whichever language loaded first.
 */
const LABEL_KEYS: Record<ThemeChoice, string> = {
  light: "theme.light",
  dark: "theme.dark",
  system: "theme.system",
};

/**
 * Keyed on the CHOICE, not the resolved theme: under `System` the button shows a display, not
 * whichever of sun/moon the OS happens to have picked. Showing the resolved icon would make the
 * three states look like two, and pressing what appears to be `Dark` would land you on `System`.
 */
const ICON: Record<ThemeChoice, ComponentType<{ className?: string }>> = {
  light: SunIcon,
  dark: MoonIcon,
  system: ComputerDesktopIcon,
};

export function ThemeToggle(): ReactNode {
  const t = useT();
  const { choice, resolved, setChoice } = useTheme();

  /*
   * No `hot` state, unlike `NavLink` and `NavAction`. Theirs exists solely to tell a Lottie player
   * which direction to play; the pill's own hover treatment — the fill and the icon's lift — is
   * `hover:` and `group-hover:` in `nav-pill.ts` and needs no React at all.
   */
  const Icon = ICON[choice];
  const next = NEXT[choice];

  /*
   * The accessible name carries three facts, and the middle one is the one a menu could not state:
   * what `System` currently means. Without it a screen-reader user pressing the button has no way to
   * tell whether following the OS is presently giving them light or dark.
   *
   * The name changes when the state does, which is the documented behaviour of a toggle button that
   * cycles: focus stays put and the new name is announced.
   */
  const state =
    choice === "system"
      ? t("theme.systemResolved", { resolved: t(LABEL_KEYS[resolved]).toLowerCase() })
      : t(LABEL_KEYS[choice]);
  /*
   * `Next: Light` rather than `Switch to light`, and the difference is not tidiness.
   *
   * Any three-state cycle reaches a point where the next state LOOKS like the current one: from
   * `System` on a light machine, the next state is `Light`, and "switch to light" then describes a
   * change the visitor can already see, which reads as a control that does nothing. Naming the MODE
   * — capitalised, matching the three words the picker actually cycles through — says what is really
   * happening: the appearance stays, and it stops following the OS.
   */
  const label = t("theme.toggleLabel", { state, next: t(LABEL_KEYS[next]) });

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={
        // Matched to the account menu's avatar trigger beside it: same box, same radius, same focus ring.
        "inline-flex size-8 cursor-pointer items-center justify-center rounded-full text-muted " +
        "transition-colors outline-none hover:bg-accent hover:text-ink " +
        "focus-visible:ring-[3px] focus-visible:ring-ring/50"
      }
      onClick={() => setChoice(next)}
    >
      {/* `aria-hidden` because the button's own label already says everything the icon means. */}
      <Icon className="size-5" aria-hidden="true" />
    </button>
  );
}
